/**
 * Leave Request form — create, edit, and detail (read-only).
 * ປະເພດລາ ວັນເລີ່ມ ວັນສິ້ນສຸດ ເຫດຜົນ; ຄິດວັນລາ (ບໍ່ນັບວັນພັກ) ອັດຕະໂນມັດ.
 */

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormInput,
  FormRoot,
  FormTextarea,
  toast,
  zodResolver,
} from "@devhop/ui";
import { CalendarRange, Download, FileText, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";
import * as RHF from "react-hook-form";
import { z } from "zod";
import type {
  CreateLeaveRequestDTO,
  LeaveRequestListItem,
  UpdateLeaveRequestDTO,
} from "@/modules/hr/leave/domain/types";
import { config } from "@/shared/lib/config";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { countLeaveDays } from "./leaveDays";
import { LeaveRequestAttachmentUpload } from "./LeaveRequestAttachmentUpload";
import type { LeaveRequestCreateFormSample } from "./leaveRequestCreateFormSamples";

/** Resolve storage key to API URL for viewing/downloading (same as contracts/monthly-report). */
function getLeaveAttachmentUrl(key: string | null | undefined): string | null {
  if (!key || typeof key !== "string" || key.trim() === "") return null;
  return `${config.apiUrl}/files/${key}`;
}

export type LeaveRequestFormValues = {
  leaveTypeId: string;
  startDate: string;
  endDate: string;
  reason: string;
  attachmentKey: string | null;
};

export type LeaveRequestFormProps =
  | {
      mode: "create";
      request?: LeaveRequestListItem | null;
      leaveTypeOptions: { value: string; label: string }[];
      /** When set, submit is disabled until an employee is selected (used by create page). */
      selectedEmployeeId?: string;
      onSubmit: (values: Omit<CreateLeaveRequestDTO, "employeeId">) => void;
      onCancel: () => void;
      isSubmitting?: boolean;
      /** When set, form shows "ເພີ່ມຂໍ້ມູນຕົວຢ່າງ" and applies samples (startDate, endDate, reason) on click. */
      autoFillGetSamples?: () => LeaveRequestCreateFormSample[];
    }
  | {
      mode: "edit";
      request: LeaveRequestListItem;
      leaveTypeOptions: { value: string; label: string }[];
      /** Available days for this leave type/year (for validation and display). */
      remainingDays?: number | null;
      onSubmit: (values: UpdateLeaveRequestDTO) => void;
      onCancel: () => void;
      isSubmitting?: boolean;
    }
  | {
      mode: "detail";
      request: LeaveRequestListItem;
      onCancel: () => void;
      onEdit?: () => void;
      leaveTypeOptions?: never;
      onSubmit?: never;
      isSubmitting?: never;
    };

function toDateInput(iso: string): string {
  const d = new Date(iso);
  return d.toISOString().slice(0, 10);
}

const LeaveRequestFormSchema = z.object({
  leaveTypeId: z.string().min(1, "ກະລຸນາເລືອກປະເພດລາ"),
  startDate: z.string().min(1, "ກະລຸນາເລືອກວັນເລີ່ມ"),
  endDate: z.string().min(1, "ກະລຸນາເລືອກວັນສິ້ນສຸດ"),
  reason: z.string(),
  attachmentKey: z.union([z.string(), z.null()]),
});

export function LeaveRequestForm(props: LeaveRequestFormProps) {
  const { mode, request } = props;
  const isCreate = mode === "create";
  const isDetail = mode === "detail";

  const defaultValues: LeaveRequestFormValues = useMemo(() => {
    if (request) {
      return {
        leaveTypeId: request.leaveTypeId,
        startDate: toDateInput(request.startDate),
        endDate: toDateInput(request.endDate),
        reason: request.reason ?? "",
        attachmentKey: request.attachmentKey ?? null,
      };
    }
    const today = new Date().toISOString().slice(0, 10);
    return {
      leaveTypeId: "",
      startDate: today,
      endDate: today,
      reason: "",
      attachmentKey: null,
    };
  }, [request]);

  const methods = RHF.useForm<LeaveRequestFormValues>({
    // biome-ignore lint/suspicious/noExplicitAny: zodResolver type inference
    resolver: zodResolver(LeaveRequestFormSchema) as any,
    defaultValues,
    values:
      request && !isCreate
        ? {
            leaveTypeId: request.leaveTypeId,
            startDate: toDateInput(request.startDate),
            endDate: toDateInput(request.endDate),
            reason: request.reason ?? "",
            attachmentKey: request.attachmentKey ?? null,
          }
        : undefined,
  });

  const startDate = methods.watch("startDate");
  const endDate = methods.watch("endDate");
  const computedDays = useMemo(() => {
    if (!startDate || !endDate) return 0;
    return countLeaveDays(startDate, endDate);
  }, [startDate, endDate]);

  const [sampleIndex, setSampleIndex] = useState(0);
  const autoFillSampleCount = useMemo(() => {
    if (!isCreate) return 0;
    const get = (props as LeaveRequestFormProps & { mode: "create" })
      .autoFillGetSamples;
    return get?.()?.length ?? 0;
  }, [isCreate, props]);
  const isEdit = mode === "edit";
  const remainingDays: number | null | undefined = isEdit
    ? (props as LeaveRequestFormProps & { mode: "edit" }).remainingDays
    : undefined;
  const remainingNum = typeof remainingDays === "number" ? remainingDays : null;
  const exceedsRemaining =
    isEdit &&
    remainingNum != null &&
    computedDays > 0 &&
    computedDays > remainingNum;

  const applyAutoFillSample = () => {
    if (mode !== "create") return;
    const getSamples = (props as LeaveRequestFormProps & { mode: "create" })
      .autoFillGetSamples;
    const samples = getSamples?.() ?? [];
    if (samples.length === 0) return;
    const s = samples[sampleIndex % samples.length];
    if (!s) return;
    methods.setValue("startDate", s.startDate);
    methods.setValue("endDate", s.endDate);
    methods.setValue("reason", s.reason);
    setSampleIndex((prev) => (prev + 1) % samples.length);
    toast.success("ເພີ່ມຂໍ້ມູນຕົວຢ່າງແລ້ວ");
  };

  const handleValidSubmit = (vals: LeaveRequestFormValues) => {
    if (isEdit && remainingNum != null && computedDays > remainingNum) return;
    const trimmed = {
      startDate: vals.startDate.trim(),
      endDate: vals.endDate.trim(),
      reason: vals.reason?.trim() || null,
      attachmentKey: vals.attachmentKey?.trim() || null,
    };
    if (isCreate) {
      (props as LeaveRequestFormProps & { mode: "create" }).onSubmit({
        leaveTypeId: vals.leaveTypeId,
        ...trimmed,
      });
    } else {
      (props as LeaveRequestFormProps & { mode: "edit" }).onSubmit(trimmed);
    }
  };

  if (isDetail && request) {
    const r = request;
    const attachmentUrl = getLeaveAttachmentUrl(r.attachmentKey);
    const attachmentFilename =
      r.attachmentKey?.split("/").pop() ?? "attachment";
    return (
      <Card className="w-full">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarRange className="h-4 w-4" />
            ຄຳຂໍລາ — {r.leaveTypeName}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-0">
          <DetailRow label="ພະນັກງານ" value={r.employeeName} />
          <DetailRow
            label="ປະເພດລາ"
            value={`${r.leaveTypeName} (${r.leaveTypeCode})`}
          />
          <DetailRow label="ວັນເລີ່ມ" value={toDateInput(r.startDate)} />
          <DetailRow label="ວັນສິ້ນສຸດ" value={toDateInput(r.endDate)} />
          <DetailRow label="ວັນລາ" value={`${r.days} ວັນ`} />
          <DetailRow label="ເຫດຜົນ" value={r.reason ?? "—"} />
          <DetailRow
            label="ເອກະສານແນບ"
            value={
              attachmentUrl ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() =>
                      window.open(
                        attachmentUrl,
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                  >
                    <FileText className="h-4 w-4" />
                    ເບິ່ງ
                  </Button>
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => {
                      const link = document.createElement("a");
                      link.href = attachmentUrl;
                      link.download = attachmentFilename;
                      link.click();
                    }}
                  >
                    <Download className="h-4 w-4" />
                    ດາວໂຫຼດ
                  </Button>
                </div>
              ) : (
                "—"
              )
            }
          />
          <DetailRow
            label="ສະຖານະ"
            value={
              r.status === "pending_line"
                ? "ລໍຖ້າຫົວໜ້າສາຍ"
                : r.status === "pending_hr"
                  ? "ລໍຖ້າ HR"
                  : r.status === "approved"
                    ? "ອະນຸມັດແລ້ວ"
                    : "ປະຕິເສດ"
            }
          />
          {r.rejectionReason && (
            <DetailRow label="ເຫດຜົນປະຕິເສດ" value={r.rejectionReason} />
          )}
        </CardContent>
        <div className="flex flex-wrap items-center justify-between gap-4 border-t px-6 py-4">
          <Button variant="outline" onClick={props.onCancel} type="button">
            ກັບຄືນ
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="mx-auto">
      <FormRoot<LeaveRequestFormValues>
        methods={methods}
        onSubmit={handleValidSubmit}
        className="w-full"
      >
        <Card className="w-full">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarRange className="h-4 w-4" />
              {isCreate ? "ຟອມຄຳຂໍລາ" : "ແກ້ໄຂຄຳຂໍລາ"}
            </CardTitle>
            <p className="text-muted-foreground text-sm">
              ວັນລາຄິດອັດຕະໂນມັດ (ບໍ່ນັບວັນພັກ)
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <RHF.Controller
              name="leaveTypeId"
              control={methods.control}
              render={({ field, fieldState }) => (
                <div className="space-y-2">
                  <label
                    htmlFor="leave-request-leaveTypeId"
                    className="font-medium text-sm"
                  >
                    ປະເພດລາ <span className="text-destructive">*</span>
                  </label>
                  <SimpleSelect
                    id="leave-request-leaveTypeId"
                    options={props.leaveTypeOptions ?? []}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="ເລືອກປະເພດລາ..."
                    className="w-full"
                    disabled={!isCreate}
                  />
                  {fieldState.error && (
                    <p className="text-destructive text-xs">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            />

            <FormInput
              name="startDate"
              label="ວັນເລີ່ມ"
              type="date"
              requiredMark
              disabled={isDetail}
            />
            <FormInput
              name="endDate"
              label="ວັນສິ້ນສຸດ"
              type="date"
              requiredMark
              disabled={isDetail}
            />

            <div className="space-y-2">
              <div className="rounded-lg border bg-muted/30 px-4 py-2">
                <span className="text-muted-foreground text-sm">
                  ວັນລາ (ບໍ່ນັບວັນພັກ):{" "}
                </span>
                <span className="font-medium tabular-nums">
                  {computedDays} ວັນ
                </span>
              </div>
              {isEdit && remainingNum != null && (
                <div className="rounded-lg border border-border px-4 py-2">
                  <span className="text-muted-foreground text-sm">
                    ສິດຄົງເຫຼືອ (ປະເພດນີ້):{" "}
                  </span>
                  <span className="font-medium tabular-nums">
                    {remainingNum} ວັນ
                  </span>
                </div>
              )}
              {exceedsRemaining && remainingNum != null && (
                <p className="text-destructive text-xs">
                  ວັນລາທີ່ຂໍ ({computedDays} ວັນ) ເກີນສິດຄົງເຫຼືອ ({remainingNum} ວັນ).
                  ກະລຸນາປັບວັນເລີ່ມ–ສິ້ນສຸດ.
                </p>
              )}
            </div>

            <FormTextarea
              name="reason"
              label="ເຫດຜົນ (optional)"
              rows={3}
              placeholder="ເຫດຜົນການລາ..."
              disabled={isDetail}
            />

            <RHF.Controller
              name="attachmentKey"
              control={methods.control}
              render={({ field }) => (
                <LeaveRequestAttachmentUpload
                  value={field.value}
                  onChange={field.onChange}
                  disabled={
                    (isCreate || isEdit) &&
                    !!(
                      props as LeaveRequestFormProps & {
                        isSubmitting?: boolean;
                      }
                    ).isSubmitting
                  }
                />
              )}
            />
          </CardContent>
        </Card>
        <div className="sticky bottom-0 z-10 mt-8 flex flex-wrap items-center justify-between gap-4 border-t bg-background/95 py-4 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <div className="flex flex-wrap items-center gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={props.onCancel}
              disabled={props.mode !== "detail" ? props.isSubmitting : false}
            >
              ຍົກເລີກ
            </Button>
            {isCreate && autoFillSampleCount > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={applyAutoFillSample}
                disabled={props.isSubmitting}
                className="gap-1.5"
              >
                <Sparkles className="h-4 w-4" />
                ຕື່ມຕົວຢ່າງ ({sampleIndex + 1}/{autoFillSampleCount})
              </Button>
            )}
          </div>
          <Button
            type="submit"
            disabled={
              props.mode !== "detail"
                ? props.isSubmitting ||
                  (isCreate &&
                    !(props as LeaveRequestFormProps & { mode: "create" })
                      .selectedEmployeeId) ||
                  (isEdit && exceedsRemaining)
                : false
            }
            isLoading={props.mode !== "detail" ? props.isSubmitting : false}
          >
            {isCreate ? "ສົ່ງຄຳຂໍ" : "ບັນທຶກ"}
          </Button>
        </div>
      </FormRoot>
    </div>
  );
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 border-border border-b py-2 last:border-0">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-sm">{value ?? "—"}</span>
    </div>
  );
}
